import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Row,
  Table,
  UncontrolledTooltip
} from 'reactstrap';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import { Link } from 'react-router-dom';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { SURVEY_STATUS_UPDATE } from 'graphql/mutations';
import useConfirmation from 'hooks/useConfirmation';
import useNotificationAlert from 'hooks/useNotificationAlert';
import Pagination from 'components/Pagination';
import usePagination from 'hooks/usePagination';
import moment from 'moment';
import { GET_USER_TEMPLATES } from 'graphql/queries';
import { SURVEY_USE_TEMPLATE } from 'graphql/mutations';
import { USER_TEMPLATE_FULL_PATH } from 'constants/paths';
import TagsFilter from './TagsFilter';
import { ADD_USER_TEMPLATE_FULL_PATH } from 'constants/paths';
import { useSelector } from 'react-redux';
import { TEMPLATE_DUPLICATE } from 'graphql/mutations';
import queryString from 'query-string';
import { useSortBy, useTable } from 'react-table';
import usePersistentSortState from 'hooks/usePersistentSortState';
import useWhiteLabel from 'hooks/useWhiteLabel';

const sortingKeys = {
  name: 'NAME',
  updatedAt: 'UPDATED_AT'
};

const isSameSortBy = (left = [], right = []) => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every(
    (item, index) => item?.id === right[index]?.id && Boolean(item?.desc) === Boolean(right[index]?.desc)
  );
};

const perPage = 10;
const Survey = (props) => {
  const { pageNumber, goToPage } = usePagination({ props });

  const { me } = useSelector((state) => state.auth);

  const { location } = props;

  const { search, pathname } = location;

  const parsedQuery = queryString.parse(search);

  const { initialTableState, persistSortBy, getOrderByFromSortBy } = usePersistentSortState({
    storageKey: 'user_template_table_state',
    sortingKeys
  });

  const [activeSortBy, setActiveSortBy] = useState(initialTableState.sortBy || []);
  const latestSortByRef = useRef(initialTableState.sortBy || []);

  const {
    loading: isLoadingDatas,
    data,
    refetch,
    networkStatus
  } = useQuery(GET_USER_TEMPLATES, {
    variables: {
      first: perPage,
      page: pageNumber,
      tags: parsedQuery.tags ?? undefined,
      orderBy: getOrderByFromSortBy(activeSortBy)
    },
    notifyOnNetworkStatusChange: true
  });

  const refetchSurveys = useCallback(
    (overrides = {}) => {
      return refetch({
        page: pageNumber,
        tags: parsedQuery.tags ?? undefined,
        orderBy: getOrderByFromSortBy(latestSortByRef.current),
        ...overrides
      });
    },
    [getOrderByFromSortBy, pageNumber, parsedQuery.tags, refetch]
  );

  const { show: showNotificationAlert } = useNotificationAlert({
    place: 'br'
  });

  const [surveyDuplicate, { loading: isLoadingSurveyDuplicate }] = useMutation(TEMPLATE_DUPLICATE);

  const [surveyStatusUpdate, { loading: isLoadingSurveyStatusUpdate }] =
    useMutation(SURVEY_STATUS_UPDATE);

  const [surveyUseTemplate, { loading: isLoadingUseTemplate }] = useMutation(SURVEY_USE_TEMPLATE);

  const { showConfirmation, hideConfirmation } = useConfirmation(
    isLoadingSurveyDuplicate || isLoadingUseTemplate || isLoadingSurveyStatusUpdate
  );

  const [totalRow, setTotalRow] = useState(0);

  const [surveys, setSurveys] = useState([]);

  useEffect(() => {
    if (data && data.getUserTemplates) {
      setTotalRow(data.getUserTemplates.paginatorInfo.total);
      setSurveys(JSON.parse(JSON.stringify(data.getUserTemplates.data)));
    }
  }, [data]);

  const onArchive = async (id) => {
    try {
      await surveyStatusUpdate({ variables: { id, status: 'ARCHIVED' } });
      showNotificationAlert({
        title: 'Success',
        message: 'You have archived the survey successfully!'
      });
      await refetchSurveys();
    } catch (err) {
      switch (err.message) {
        default:
          showNotificationAlert({
            type: 'danger',
            title: 'Failed',
            message: err.message
          });
          break;
      }
    } finally {
      hideConfirmation();
    }
  };

  const onDuplicate = useCallback(async (surveyId) => {
    try {
      await surveyDuplicate({ variables: { surveyId } });
      showNotificationAlert({
        title: 'Success',
        message: 'You have duplicated the survey successfully!'
      });
      await refetchSurveys();
    } catch (err) {
      switch (err.message) {
        default:
          showNotificationAlert({
            type: 'danger',
            title: 'Failed',
            message: err.message
          });
          break;
      }
    } finally {
      hideConfirmation();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTagsChange = (data) => {
    const url = queryString.stringifyUrl({
      url: pathname,
      query: { ...parsedQuery, page: 1, tags: data.map((elm) => elm.name) }
    });

    props.history.push(url);

    refetchSurveys({
      tags: data.map((elm) => elm.name),
      page: 1
    });
  };

  const onUseTemplate = (surveyId) => {
    showConfirmation({
      title: (
        <div>
          Are you sure you want to use this template?
          <br />
          It will download to your "My Area" for use.
        </div>
      ),
      onConfirm: async () => {
        try {
          await surveyUseTemplate({ variables: { surveyId } });
          showNotificationAlert({
            title: 'Success',
            message: 'Success!'
          });
          await refetchSurveys();
        } catch (err) {
          switch (err.message) {
            default:
              showNotificationAlert({
                type: 'danger',
                title: 'Failed',
                message: err.message
              });
              break;
          }
        } finally {
          hideConfirmation();
        }
      },
      confirmTitle: 'Use Template'
    });
  };

  const columns = useMemo(() => {
    const sortIcon = (column) =>
      column.isSorted ? (
        column.isSortedDesc ? (
          <span className="sorted-icon sorted-icon__desc" />
        ) : (
          <span className="sorted-icon sorted-icon__asc" />
        )
      ) : (
        <span className="sorted-icon" />
      );

    const cols = [
      {
        Header: ({ column }) => (
          <span className="heading-w-icon">Template Name {sortIcon(column)}</span>
        ),
        accessor: 'name'
      },
      {
        Header: () => 'Tags',
        id: 'tags',
        disableSortBy: true,
        Cell: ({ cell }) =>
          cell.row.original.tags ? cell.row.original.tags.map((elm) => elm.name).join(', ') : ''
      },
      {
        Header: ({ column }) => (
          <span className="heading-w-icon">Last Edited {sortIcon(column)}</span>
        ),
        accessor: 'updatedAt',
        Cell: ({ value }) =>
          value ? <span className="text-muted">{moment(value).format('DD/MM/YYYY')}</span> : ''
      },
      {
        Header: () => 'Time Used',
        accessor: 'timeUsed',
        disableSortBy: true
      }
    ];

    if (me?.visibility === 'FULL') {
      cols.push({
        Header: () => 'Manage',
        id: 'action',
        disableSortBy: true,
        Cell: ({ cell }) => {
          const rowData = cell.row.original;
          return (
            <div className="d-flex">
              <Link
                className="table-action"
                to={`${USER_TEMPLATE_FULL_PATH}/${rowData.id}/edit`}
                id={`tooltip-edit-${rowData.id}`}>
                <i className="fas fa-user-edit" />
              </Link>
              <UncontrolledTooltip delay={0} target={`tooltip-edit-${rowData.id}`}>
                Edit Template
              </UncontrolledTooltip>

              <button
                className="table-action table-action-duplicate"
                id={`tooltip-duplicate-${rowData.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  showConfirmation({
                    title: `Are you sure want to duplicate "${rowData.name}"?`,
                    onConfirm: () => onDuplicate(rowData.id),
                    confirmTitle: 'Duplicate'
                  });
                }}>
                <i className="ni ni-ungroup" />
              </button>
              <UncontrolledTooltip delay={0} target={`tooltip-duplicate-${rowData.id}`}>
                Duplicate
              </UncontrolledTooltip>

              <button
                className="table-action table-action-template"
                id={`tooltip-template-${rowData.id}`}
                onClick={() => onUseTemplate(rowData.id)}>
                <i className="ni ni-active-40" />
              </button>
              <UncontrolledTooltip delay={0} target={`tooltip-template-${rowData.id}`}>
                Use this Template
              </UncontrolledTooltip>

              <button
                className="table-action table-action-archive"
                id={`tooltip-archive-${rowData.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  showConfirmation({
                    title: 'Are you sure you want to archive this template?',
                    confirmTitle: 'Archive Template',
                    onConfirm: () => onArchive(rowData.id)
                  });
                }}>
                <i className="ni ni-archive-2" />
              </button>
              <UncontrolledTooltip delay={0} target={`tooltip-archive-${rowData.id}`}>
                Archive Template
              </UncontrolledTooltip>
            </div>
          );
        }
      });
    }

    return cols;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state: tableState } =
    useTable(
      {
        columns,
        data: surveys,
        manualSortBy: true,
        disableSortRemove: true,
        initialState: initialTableState
      },
      useSortBy
    );

  useEffect(() => {
    const nextSortBy = tableState.sortBy || [];

    latestSortByRef.current = nextSortBy;
    persistSortBy(nextSortBy);

    if (!isSameSortBy(activeSortBy, nextSortBy)) {
      setActiveSortBy(nextSortBy);
    }
  }, [activeSortBy, persistSortBy, tableState.sortBy]);

  const { isWhiteLabelTeam } = useWhiteLabel();

  return (
    <>
      <SimpleHeader
        name="List User Templates"
        description={
          <>
            <p>
              {!isWhiteLabelTeam ? 'See the default Gather' : 'See a list of your own'} templates
              below. Simply select from the icons as to whether you want to: use the template or
              view its content.
            <br />
              To use the template ensure you use the "Use this template" icon, this will add it to
              "My Area" where you can then publish it for use.
            </p>
          </>
        }
        firstColProps={{ xs: 12 }}
        secondColProps={{ xs: 12, className: 'mt-3 mt-sm-0' }}
        rightElements={
          me?.visibility === 'FULL' && (
            <Link
              to={ADD_USER_TEMPLATE_FULL_PATH}
              className="btn btn-neutral btn-sm rounded-pill flex-grow-1 flex-sm-grow-0"
              color="default"
              size="sm">
              New Template
            </Link>
          )
        }
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <CardHeader className="border-0">
                <Row>
                  <Col>
                    <h3 className="mb-0 text-brand">User Templates</h3>
                  </Col>
                  <Col>
                    <TagsFilter
                      values={
                        parsedQuery.tags
                          ? Array.isArray(parsedQuery.tags)
                            ? parsedQuery.tags.map((elm) => ({ id: elm, name: elm }))
                            : [{ id: parsedQuery.tags, name: parsedQuery.tags }]
                          : []
                      }
                      onChange={onTagsChange}
                    />
                  </Col>
                </Row>
              </CardHeader>
              {(isLoadingDatas || networkStatus === NetworkStatus.refetch) && (
                <CardBody>
                  <p className="text-center">Loading...</p>
                </CardBody>
              )}

              {!isLoadingDatas &&
                networkStatus !== NetworkStatus.refetch &&
                data?.getUserTemplates?.data.length <= 0 && (
                  <CardBody>
                    <p className="text-center">There is no data!</p>
                  </CardBody>
                )}

              {!isLoadingDatas &&
                networkStatus !== NetworkStatus.refetch &&
                data?.getUserTemplates?.data.length >= 1 && (
                  <>
                    <Table
                      className="align-items-center table-flush"
                      responsive
                      striped
                      {...getTableProps()}>
                      <thead className="thead-light">
                        {headerGroups.map((headerGroup) => (
                          <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                              <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                {column.render('Header')}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody {...getTableBodyProps()}>
                        {rows.map((row) => {
                          prepareRow(row);
                          return (
                            <tr {...row.getRowProps()}>
                              {row.cells.map((cell) => (
                                <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    <CardFooter className="py-4">
                      <Pagination
                        totalRow={totalRow}
                        perPage={perPage}
                        initialPage={pageNumber - 1}
                        onPageChange={(data) => {
                          goToPage(data);
                        }}
                      />
                    </CardFooter>
                  </>
                )}
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default Survey;
